/**
 * Overlays — the ⌘K composer shell (line 4003), the confirm dialog (line 3916) and the toast (line 3987).
 * They sit outside the five canonical screens but are part of the app chrome the header/dashboard establish.
 */
import type { ReactNode } from 'react';
import { css } from './style';
import { CARD_BASE } from './surfaces';
import type { Handler } from './controls';

export function Dialog({
  label,
  width = 360,
  children,
  onScrimClick,
}: {
  label: string;
  width?: number;
  children?: ReactNode;
  onScrimClick?: Handler;
}) {
  return (
    <div
      onClick={onScrimClick}
      className="pf-scrim"
      style={css(
        'position:fixed;inset:0;z-index:80;background:color-mix(in srgb, var(--rail) 45%, transparent);display:flex;align-items:center;justify-content:center;padding:24px',
      )}
    >
      <div
        role="dialog"
        aria-label={label}
        className="pf-modal"
        style={css(
          `${CARD_BASE};box-shadow:var(--shadow-float);width:${width}px;max-width:100%;padding:18px 20px`,
        )}
      >
        {children}
      </div>
    </div>
  );
}
export function Toast({
  icon,
  iconColor,
  children,
}: {
  icon: string;
  iconColor: string;
  children?: ReactNode;
}) {
  return (
    <div
      role="status"
      aria-live="polite"
      style={css(
        `position:fixed;bottom:24px;left:50%;transform:translateX(-50%);z-index:90;${CARD_BASE};box-shadow:var(--shadow-float);display:flex;align-items:center;gap:9px;padding:11px 16px;max-width:min(440px,90vw)`,
      )}
    >
      <i className={`ph ${icon}`} style={css(`font-size:15px;color:${iconColor};flex:none`)}></i>
      <span style={css('font-size:11.5px;color:var(--text-mid);line-height:1.5')}>{children}</span>
    </div>
  );
}
export function ComposerShell({
  title = 'Ask PointFive',
  shortcut = '⌘K',
  onClose,
  children,
}: {
  title?: string;
  shortcut?: string;
  onClose?: Handler;
  children?: ReactNode;
}) {
  return (
    <div
      style={css(
        'width:360px;background:var(--surface);border:1px solid var(--l10);border-radius:16px;box-shadow:var(--shadow-float);overflow:hidden',
      )}
    >
      <div
        style={css(
          'display:flex;align-items:center;gap:9px;padding:13px 15px;border-bottom:1px solid var(--l7)',
        )}
      >
        <div
          style={css(
            'width:22px;height:22px;border-radius:6px;background:linear-gradient(135deg,var(--sky-fill),var(--sky));display:flex;align-items:center;justify-content:center',
          )}
        >
          <i className="ph-fill ph-sparkle" style={css('font-size:12px;color:var(--bg)')}></i>
        </div>
        <span style={css('font-size:12.5px;font-weight:600')}>{title}</span>
        <span
          style={css(
            'font-size:9px;font-weight:600;letter-spacing:0.06em;padding:2px 7px;border-radius:99px;border:1px solid var(--l12);color:var(--muted)',
          )}
        >
          {shortcut}
        </span>
        <div style={css('flex:1')}></div>
        {onClose ? (
          <button
            onClick={onClose}
            aria-label="Close composer"
            style={css(
              'width:24px;height:24px;border:none;background:transparent;color:var(--muted2);cursor:pointer;border-radius:6px',
            )}
          >
            <i className="ph ph-x" style={css('font-size:13px')}></i>
          </button>
        ) : null}
      </div>
      {children}
    </div>
  );
}
